import React, { PropsWithChildren } from 'react';
import { GMTooltip } from '../../../CommonControls/Tooltip/Tooltip';
import { versionNumber } from '../../../CommonUtils/Version';
import { useLocalization } from '../../../Contexts/Localization';
import Logo from '../../../Assets/Images/SidebarLogo.png';
import css from './Sidebar.module.scss';
import classNames from 'classnames';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppRoutes } from '../../../CommonUtils/Routes';
import { useLogout, useUserState } from '../../../Contexts/User';
import { formatUserInitials } from '../../../CommonUtils/Formatter';
import { GMAvatar } from '../../../CommonControls/Avatar/Avatar';
import LogoutIcon from '../../../Assets/Icons/logout.svg?react';
import { GMIconButton } from '../../../CommonControls/Buttons/IconButton';
import { Heading3Text } from '../../../CommonControls/Text/Text';
import { Drawer } from '@mui/material';
import { useIsTablet } from '../../../CommonUtils/MediaQueries';
import Help from '../../../Assets/Icons/help.svg?react';

export const Sidebar: React.FC<PropsWithChildren> = (props) => {
    return (
        <div className={css.root}>
            {props.children}
        </div>
    );
};

export const SidebarUserPreferences: React.FC = () => {
    const Localization = useLocalization();
    const location = useLocation();
    const navigate = useNavigate();
    const { UserData } = useUserState();
    const isTablet = useIsTablet();

    const preferencesRouteActive: boolean = location.pathname.includes(AppRoutes.Preferences);

    return (
        !isTablet ?
            <div
                className={classNames(css.profileSection, preferencesRouteActive ? css.active : '')}
                onClick={() => (!preferencesRouteActive ? navigate(AppRoutes.Preferences, { state: location.pathname }) : null)}
            >
                <GMTooltip
                    text={Localization.UserSettings}
                >
                    <div>
                        <GMAvatar
                            imageSrc={UserData.profile_picture}
                            letters={!UserData.profile_picture ? formatUserInitials(UserData.firstname, UserData.lastname, UserData.username) : undefined}
                        />
                    </div>
                </GMTooltip>
            </div> : null
    );
};

export const SidebarDivider: React.FC = () => {
    return (
        <div className={css.divider} />
    );
};

export const SidebarTopSection: React.FC<PropsWithChildren> = (props) => {
    return (
        <div className={css.topSection}>
            {props.children}
        </div>
    );
};

export const SidebarBottomSection: React.FC<PropsWithChildren> = (props) => {
    return (
        <div className={css.bottomSection}>
            {props.children}
        </div>
    );
};

export const SidebarLogout: React.FC = () => {
    const Localization = useLocalization();
    const logout = useLogout();
    const isTablet = useIsTablet();

    return (
        !isTablet ?
            <SidebarIcon
                position={'bottom'}
                show
                active={false}
                tooltip={Localization.Logout}
                onClick={() => logout(false)}
                icon={<LogoutIcon className={classNames(css.icon, css.powerIcon)} />}
            />
            : null
    );
};

export const SidebarHelp: React.FC = () => {
    const Localization = useLocalization();
    const location = useLocation();
    const isTablet = useIsTablet();
    const navigate = useNavigate();

    const helpRouteActive: boolean = location.pathname.includes(AppRoutes.Help);

    return (
        !isTablet ?
            <SidebarIcon
                show
                active={helpRouteActive}
                tooltip={Localization.Help}
                onClick={() => navigate(AppRoutes.Help)}
                icon={<Help className={css.icon} />}
            /> : null
    );
};

export const SidebarLogo: React.FC = () => {
    const Localization = useLocalization();
    const isTablet = useIsTablet();

    return (
        !isTablet ?
            <div className={css.logoSection}>
                <div>
                    {Localization.Version} {versionNumber()}
                </div>
                <img
                    className={css.logo}
                    src={Logo}
                    alt={Localization.KeyTrak}
                />
            </div> : null
    );
};

export interface SidebarIconProps {
    /**
     * Whether the icon is shown
     */
    show: boolean;
    /**
     * Whether the icon is disabled
     */
    disabled?: boolean;
    /**
     * Whether the icon route is active
     */
    active: boolean;
    /**
     * Hover tooltip label
     */
    tooltip: string;
    /**
     * Callback when clicked
     */
    onClick: () => void;
    /**
     * Icon to display
     */
    icon: JSX.Element;
    /**
     * This option will set the svg path stroke color
     */
    colorPathStroke?: boolean;
    /**
     * This option will set the svg path fill color
     */
    colorPathFill?: boolean;
    /**
     * Disabls the onClick callback when active is true. Default 'true'
     */
    disableClickWhenActive?: boolean;
    /**
     * Icon position. Default 'top'
     */
    position?: 'top' | 'bottom';
    /**
     * Increases left and right padding for the icon when it's displayed in the bottom tablet bar
     */
    extraTabletPadding?: boolean;
}

export const SidebarIcon: React.FC<SidebarIconProps> = ({ disableClickWhenActive = true, position = 'top', ...props }) => {
    const isTablet: boolean = useIsTablet();

    return props.show ?
        <div
            className={classNames(
                css.navItem,
                props.active ? css.active : '',
                position === 'bottom' ? css.bottomNavItem : '',
                position !== 'bottom' && !isTablet ? css.sideNavItem : '',
                props.extraTabletPadding ? css.extraTabletPadding : '',
            )}
        >
            <GMIconButton
                color={isTablet ? 'light' : 'secondary'}
                tooltip={props.tooltip}
                onClick={props.onClick}
                active={props.active}
                disableClick={props.active && disableClickWhenActive}
                className={css.iconButton}
                disableRipple
                colorPathStroke={props.colorPathStroke}
                disabled={props.disabled}
                colorPathFill={props.colorPathFill}
            >
                {props.icon}
            </GMIconButton>
        </div> : null;
};

interface ComponentProps extends PropsWithChildren {
    /**
     * If the drawer is open
     */
    open?: boolean;
    /**
     * Callback to close drawer
     */
    onClose: () => void;
}

export const SidebarDrawer: React.FC<ComponentProps> = (props) => {
    const isTablet = useIsTablet();

    return (
        <Drawer
            classes={{
                root: css.drawer,
                paper: css.paper,
            }}
            anchor={isTablet ? 'bottom' : 'left'}
            open={props.open}
            onClose={() => props.onClose()}
        >
            <div className={css.drawerContent}>
                {props.children}
            </div>
        </Drawer>
    );
};

interface DrawerTitleProps {
    /**
     * Title label text
     */
    label: string;
}

export const DrawerTitle: React.FC<DrawerTitleProps> = (props) => {
    return (
        <div className={css.drawerTitle}>
            <Heading3Text>
                {props.label}
            </Heading3Text>
        </div>
    );
};

interface DrawerLinkProps {
    /**
     * Link label text
     */
    label: string;
    /**
     * Callback when link is clicked
     */
    onClick: () => void;
}

export const DrawerLink: React.FC<DrawerLinkProps> = (props) => {
    return (
        <div className={css.linkContainer}>
            <div
                className={css.link}
                onClick={props.onClick}
            >
                {props.label}
            </div>
        </div>
    );
};